import { Card, CardContent } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
} from "@/components/ui/chart";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

const chartData = [
  { month: "Gmail", Inbox: 100, Spam: 0, Unreceived: 0, Total: 5 },
  { month: "Outlook", Inbox: 50, Spam: 50, Unreceived: 0, Total: 5 },
  { month: "Yahoo", Inbox: 70, Spam: 20, Unreceived: 10, Total: 5 },
  { month: "AOL", Inbox: 90, Spam: 10, Unreceived: 0, Total: 5 },
  { month: "iCloud", Inbox: 50, Spam: 50, Unreceived: 0, Total: 5 },
  { month: "GMX", Inbox: 100, Spam: 0, Unreceived: 0, Total: 5 },
  { month: "ProtonMail", Inbox: 100, Spam: 0, Unreceived: 0, Total: 5 },
];

const chartConfig = {
  Inbox: {
    label: "Inbox",
    color: "#86efac",
  },
  Spam: {
    label: "Spam",
    color: "#ef4444",
  },
  Unreceived: {
    label: "Unreceived",
    color: "#eab308",
  },
} satisfies ChartConfig;

export function EmailPlacementChart({
  chartData,
}: {
  chartData: {
    month: string;
    Inbox: number;
    Spam: number;
    Unreceived: number;
    Total: number;
  }[];
}) {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 2 });

  // Calculate visible data for small screens
  const visibleChartData = useMemo(() => {
    // For medium screens and above, show all data
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      return chartData;
    }

    // For small screens, show only the visible range
    return chartData.slice(visibleRange.start, visibleRange.end);
  }, [chartData, visibleRange]);

  // Scroll navigation functions
  const scrollNext = () => {
    if (visibleRange.end < chartData.length) {
      setVisibleRange((prev) => ({
        start: prev.start + 1,
        end: prev.end + 1,
      }));
    }
  };

  const scrollPrev = () => {
    if (visibleRange.start > 0) {
      setVisibleRange((prev) => ({
        start: prev.start - 1,
        end: prev.end - 1,
      }));
    }
  };

  // Add screen size listener
  useEffect(() => {
    const handleResize = () => {
      // Reset to full view on large screens
      if (window.innerWidth >= 768) {
        setVisibleRange({ start: 0, end: chartData.length });
      } else {
        // Ensure we're showing 3 items on small screens
        setVisibleRange({ start: 0, end: 2 });
      }
    };

    // Initial setup
    handleResize();

    // Add listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [chartData.length]);

  return (
    <Card className="w-full">
      <CardContent className="p-3">
        {/* navigation button for mobile screen */}
        <div className="md:hidden flex justify-between items-center mb-2">
          <button
            onClick={scrollPrev}
            disabled={visibleRange.start <= 0}
            className="p-1 rounded-full bg-gray-100 dark:bg-gray-800 disabled:opacity-30"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-xs text-gray-500">
            Showing {visibleRange.start + 1}-
            {Math.min(visibleRange.end, chartData.length)} of {chartData.length}
          </div>
          <button
            onClick={scrollNext}
            disabled={visibleRange.end >= chartData.length}
            className="p-1 rounded-full bg-gray-100 dark:bg-gray-800 disabled:opacity-30"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="w-full">
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <ChartContainer config={chartConfig} className="w-full h-full">
                <BarChart
                  accessibilityLayer
                  data={visibleChartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    height={50}
                    tick={{ fontSize: 12 }}
                    interval={0}
                    tickFormatter={(value) => {
                      // Wrap long provider names
                      return value.length > 15
                        ? value.slice(0, 15) + "..."
                        : value;
                    }}
                  />
                  <YAxis
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    domain={[0, 100]}
                    unit="%"
                    allowDataOverflow={false}
                    tick={{ fontSize: 12 }}
                  />
                  <ChartTooltip
                    content={({ active, payload, label }) => {
                      if (active && payload?.length) {
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-lg min-w-[150px]">
                            <div className="grid gap-2">
                              <div className="flex flex-col mb-1">
                                <span className="font-semibold text-foreground">
                                  {label}
                                </span>
                              </div>
                              {payload.map((entry) => (
                                <div
                                  key={entry.name}
                                  className="flex items-center text-xs text-muted-foreground"
                                >
                                  <div className="flex items-center">
                                    <div
                                      className="w-2.5 h-2.5 rounded-sm mr-1.5"
                                      style={{ backgroundColor: entry.color }}
                                    />
                                    {chartConfig[
                                      entry.name as keyof typeof chartConfig
                                    ]?.label || entry.name}
                                  </div>
                                  <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                    {entry.value}
                                    <span className="font-normal text-muted-foreground">
                                      %
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar
                    dataKey="Inbox"
                    stackId="a"
                    fill="#22c55e"
                    radius={[0, 0, 4, 4]}
                    barSize={40}
                  />
                  <Bar
                    dataKey="Spam"
                    stackId="a"
                    fill="#ef4444"
                    radius={[0, 0, 0, 0]}
                    barSize={40}
                  />
                  <Bar
                    dataKey="Unreceived"
                    stackId="a"
                    fill="#eab308"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ChartContainer>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
